import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "eventera.collectionsPath";

// The collections sidebar shows one level at a time, so its whole navigation
// state is the path down to that level: collection ids, outermost first, empty
// at the top. Keyed by space — browsing someone else's collections must not
// overwrite where you were in your own.
type Stored = Record<string, string[]>;

// Two caps, because nothing here is pruned against a live tree: a path naming a
// collection since deleted or re-parented simply stops resolving, and the level
// it reached is shown instead. Both bounds are far above any real list.
const MAX_SPACES = 5;
const MAX_DEPTH = 32;

function readStored(): Stored {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Stored) : {};
  } catch {
    return {};
  }
}

function readSpace(space: string): string[] {
  const path = readStored()[space];
  return Array.isArray(path) ? path.slice(0, MAX_DEPTH) : [];
}

function writeSpace(space: string, path: string[]) {
  try {
    const stored = readStored();
    delete stored[space];
    // Object key order is insertion order for string keys, so re-inserting the
    // active space last makes the oldest entry the one that falls off.
    const spaces = Object.keys(stored).slice(-(MAX_SPACES - 1));
    const next: Stored = {};
    for (const other of spaces) next[other] = stored[other];
    next[space] = path.slice(0, MAX_DEPTH);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // A full or disabled storage costs the user persistence, not the view.
  }
}

const same = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, index) => id === b[index]);

/**
 * Which level of the collection graph the sidebar is showing, persisted to
 * localStorage.
 *
 * Deliberately not in Redux: nothing outside the sidebar reads it, and it has to
 * survive a reload, which Redux state here does not. Same reasoning as
 * {@link useRecordsViewOptions}.
 *
 * There is one setter rather than `open`/`up` helpers on purpose. The caller
 * moves relative to the path it actually *resolved* (see `resolveLevel`), not
 * the one it stored, so a stale segment cannot leave "back" pointing at the
 * level already on screen.
 */
export function useCollectionPath(space: string) {
  const [state, setState] = useState(() => ({
    space,
    path: readSpace(space),
  }));

  // Switching spaces swaps in that space's saved path, rather than carrying the
  // current one over and then saving it under the wrong key.
  if (state.space !== space) {
    setState({ space, path: readSpace(space) });
  }

  useEffect(() => {
    writeSpace(state.space, state.path);
  }, [state]);

  const goTo = useCallback(
    (path: string[]) =>
      setState((previous) =>
        // Re-rendering for a move that changes nothing would restart the write
        // effect and every downstream memo for no reason.
        same(previous.path, path)
          ? previous
          : { ...previous, path: path.slice(0, MAX_DEPTH) },
      ),
    [],
  );

  return { path: state.path, goTo };
}
